import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateWorkTime } from '@/hooks/schedules/useScheduleTemplates';
import { Clock, Loader2, CheckCircle } from 'lucide-react';
import type { ScheduleTemplate } from '@/types/schedule.types';

interface EditWorkTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: ScheduleTemplate | null;
}

export function EditWorkTimeDialog({
  open,
  onOpenChange,
  template,
}: EditWorkTimeDialogProps) {
  const [shiftStart, setShiftStart] = useState('09:00');
  const [shiftEnd, setShiftEnd] = useState('18:00');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const updateWorkTime = useUpdateWorkTime();

  // Initialize from template when opened
  useEffect(() => {
    if (template && open) {
      // Try to get current time from days_pattern or use defaults
      const pattern = template.days_pattern;

      if (pattern) {
        // Check for shift pattern
        if ('work_time' in pattern && pattern.work_time) {
          setShiftStart(pattern.work_time.start || '09:00');
          setShiftEnd(pattern.work_time.end || '18:00');
        }
        // Check for weekly pattern - get first working day time
        else if ('monday' in pattern || 'tuesday' in pattern) {
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          for (const day of days) {
            const dayPattern = (pattern as Record<string, any>)[day];
            if (dayPattern?.isWorking && dayPattern?.start && dayPattern?.end) {
              const start = String(dayPattern.start);
              const end = String(dayPattern.end);
              setShiftStart(start.substring(0, 5));
              setShiftEnd(end.substring(0, 5));
              break;
            }
          }
        }
        // Check for custom pattern - get first working day time
        else if ('pattern' in pattern && Array.isArray((pattern as any).pattern)) {
          const workingDay = (pattern as any).pattern.find((p: any) => p.isWorking && p.start && p.end);
          if (workingDay && workingDay.start && workingDay.end) {
            const start = String(workingDay.start);
            const end = String(workingDay.end);
            setShiftStart(start.substring(0, 5));
            setShiftEnd(end.substring(0, 5));
          }
        }
      }

      setError(null);
      setSuccess(null);
    }
  }, [template, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!template) return;

    // Validate time format
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(shiftStart)) {
      setError('Неверный формат времени начала (HH:MM)');
      return;
    }
    if (!timeRegex.test(shiftEnd)) {
      setError('Неверный формат времени окончания (HH:MM)');
      return;
    }

    try {
      const result = await updateWorkTime.mutateAsync({
        templateId: template.id,
        shiftStart,
        shiftEnd,
      });

      setSuccess(`Обновлено ${result.updated_days} рабочих дней${result.is_night_shift ? ' (ночная смена)' : ''}`);

      // Close dialog after short delay to show success message
      setTimeout(() => {
        onOpenChange(false);
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Ошибка при обновлении времени');
    }
  };

  // Check if time crosses midnight
  const crossesMidnight = () => {
    if (!shiftStart || !shiftEnd) return false;
    const [startH] = shiftStart.split(':').map(Number);
    const [endH] = shiftEnd.split(':').map(Number);
    return endH < startH;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Изменить время работы
          </DialogTitle>
          <DialogDescription>
            {template?.name}
            {template?.code_1c && (
              <span className="block text-xs text-blue-600 mt-1">
                Шаблон из 1С - изменение времени обновит все рабочие дни в календаре
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="shift_start">Начало смены</Label>
                <Input
                  id="shift_start"
                  type="time"
                  value={shiftStart}
                  onChange={(e) => setShiftStart(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="shift_end">Окончание смены</Label>
                <Input
                  id="shift_end"
                  type="time"
                  value={shiftEnd}
                  onChange={(e) => setShiftEnd(e.target.value)}
                  required
                />
              </div>
            </div>

            {crossesMidnight() && (
              <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 p-3 text-sm text-amber-800 dark:text-amber-200">
                Смена пересекает полночь ({shiftStart} → {shiftEnd} следующего дня)
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-950/20 p-3 text-sm text-red-800 dark:text-red-200">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-md bg-green-50 dark:bg-green-950/20 p-3 text-sm text-green-800 dark:text-green-200 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                {success}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateWorkTime.isPending}
            >
              Отмена
            </Button>
            <Button type="submit" disabled={updateWorkTime.isPending || !!success}>
              {updateWorkTime.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Сохранить
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
